import React from 'react';
import {Icon} from '@lightricks/react-design-system';
import {Box, styled, Avatar} from '@mui/material';
import {SocialPlatform} from '@/types/platforms';
import translate from '@/utils/translate';
import ErrorCircleIcon from '@/components/icons/ErrorCircle';
import LEGACY_COLORS from '@/styles/legacyColors';

export interface ConnectedAccountData {
  provider: string;
  cachedProfileImageUrl?: string | null;
  cachedHandle?: string | null;
  needsRelink?: boolean;
}

export interface ConnectedSocialAccountProps {
  platform: SocialPlatform;
  accountData: ConnectedAccountData;
  onUnlink: (provider: string) => void;
  isError?: boolean;
  onRelink?: (provider: string) => void;
  isLoading?: boolean;
}

const shouldNotForwardProps = ['needsRelink'];

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{needsRelink: boolean}>(({needsRelink}) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 0 8px 8px',
  borderBottom: `solid 1px ${LEGACY_COLORS.GRAY_LIGHTER}`,
  backgroundColor: 'transparent',

  // Add background checkmark for connected accounts (non-error state)
  ...(!needsRelink && {
    background:
      "url('/assets/svg/icon-check-mark-green.svg') right center no-repeat",
  }),
}));

const StyledAvatarContainer = styled(Box)(() => ({
  position: 'relative',
  display: 'inline-block',
  marginRight: '15px',
}));

const StyledAvatar = styled(Avatar)(({src}) => ({
  width: '40px',
  height: '40px',
  fontSize: '30px',
  color: src ? LEGACY_COLORS.GRAY : '#ffffff',
  backgroundColor: src ? '#eeeeee' : LEGACY_COLORS.GRAY,
  borderRadius: '0px',
}));

const StyledNetworkBadge = styled(Box)<{platform: SocialPlatform}>(
  ({platform}) => {
    const getBadgeImage = (platformType: SocialPlatform) => {
      const badgeImages = {
        Instagram: "url('/assets/svg/logo-instagram-gray.svg')",
        Pinterest: "url('/assets/svg/logo-pinterest-gray.svg')",
        YouTube: "url('/assets/svg/logo-youtube-gray.svg')",
        Facebook: "url('/assets/svg/logo-facebook-gray.svg')",
        TikTok: "url('/assets/svg/logo-tiktok-dark-gray.svg')",
      } as const;
      return badgeImages[platformType];
    };

    return {
      'position': 'absolute',
      'bottom': '-6px',
      'right': '-12px',
      'width': '26px',
      'height': '26px',
      'backgroundColor': 'white',
      'borderRadius': '6px',
      'backgroundImage': getBadgeImage(platform),
      'backgroundSize': '16px',
      'backgroundRepeat': 'no-repeat',
      'backgroundPosition': 'center center',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      },
    };
  }
);

const StyledAccountName = styled(Box)(() => ({
  fontSize: '17px',
  lineHeight: '22px',
  fontWeight: 'normal',
  color: LEGACY_COLORS.GRAY,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  gap: '3px',
}));

const StyledControls = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  justifyContent: 'flex-end',
  textAlign: 'right',
}));

const StyledRelinkButton = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  color: LEGACY_COLORS.GRAY,
  fontSize: '12px',
  gap: '3px',
  cursor: 'pointer',
  padding: 0,
}));

function ConnectedSocialAccount({
  platform,
  accountData,
  onUnlink,
  isError = false,
  onRelink,
  isLoading = false,
}: ConnectedSocialAccountProps) {
  const needsRelink = Boolean(isError || accountData.needsRelink);
  const displayName = accountData.cachedHandle || `${platform} Account`;

  const handleUnlink = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoading) {
      onUnlink(accountData.provider);
    }
  };

  const handleRelink = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoading && onRelink) {
      onRelink(accountData.provider);
    }
  };

  return (
    <StyledContainer needsRelink={needsRelink}>
      <StyledAvatarContainer>
        <StyledAvatar
          src={accountData.cachedProfileImageUrl || ''}
          variant="rounded"
        >
          {accountData.cachedProfileImageUrl
            ? ' '
            : displayName.charAt(0).toUpperCase()}
        </StyledAvatar>
        <StyledNetworkBadge platform={platform} />
      </StyledAvatarContainer>

      <StyledAccountName onClick={handleUnlink} role="button">
        {displayName}
        {needsRelink && (
          <ErrorCircleIcon width={18} height={18} color="#ff3b30" />
        )}
      </StyledAccountName>

      {needsRelink && onRelink && (
        <StyledControls>
          <StyledRelinkButton
            onClick={handleRelink}
            role="button"
            title={translate('Relink')}
          >
            {translate('Relink')}
            <Icon
              size="medium"
              appearance="neutral"
              color={LEGACY_COLORS.GRAY}
              name="Actions-Reset"
              style={{
                width: '20px',
                height: '20px',
                transform: 'rotate(90deg) scaleX(-1)',
              }}
            />
          </StyledRelinkButton>
        </StyledControls>
      )}
    </StyledContainer>
  );
}

export default ConnectedSocialAccount;
